import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { Pencil, Trash } from 'lucide-react';
import { SearchBar } from '../SearchBar/SearchBar';
import { Modal } from '../Modal/Modal';
import { StockForm } from '../StockForm/StockForm';
import { api } from '../../services/api';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

export function StockModule({ materials, warehouses = [], onAdd, onUpdate, onDelete }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [warehouseFilter, setWarehouseFilter] = useState('all'); // 'all' | 'popayan' | 'santander'
  const [categoryFilter, setCategoryFilter] = useState('Todos');
  const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const formatMoney = (n) => {
    const v = Number(n || 0);
    try {
      return v.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
    } catch (_) {
      return '$' + String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  };
  const normalizeWarehouseKey = (v) => {
    const s = String(v || '').toLowerCase().trim();
    const noAcc = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    let slug = noAcc.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (/^bodega-/.test(slug)) slug = slug.replace(/^bodega-/, '');
    if (/^warehouse-/.test(slug)) slug = slug.replace(/^warehouse-/, '');
    return slug || noAcc || s;
  };
  const [showSuggested] = useState(false);
  const [materialStatus, setMaterialStatus] = useState(null);
  const [serialsOpen, setSerialsOpen] = useState(false);
  const [serialsRows, setSerialsRows] = useState([]);
  const [serialsMaterial, setSerialsMaterial] = useState(null);
  const [nonSerialInfo, setNonSerialInfo] = useState(null);
  const [serialsTotal, setSerialsTotal] = useState(0);
  const [serialsEdits, setSerialsEdits] = useState({});
  const [serialsFilter, setSerialsFilter] = useState({ serial: '', brand: '', warehouse: '', cedula: '' });
  const [serialsCedulasMap, setSerialsCedulasMap] = useState({});
  const [serialsNamesByDni, setSerialsNamesByDni] = useState({});
  const [serialsDatesMap, setSerialsDatesMap] = useState({});
  const [serialsMovDatesById, setSerialsMovDatesById] = useState({});
  const [serialsStatus, setSerialsStatus] = useState(null);
  const [stockAllOpen, setStockAllOpen] = useState(false);
  const [stockAllRows, setStockAllRows] = useState([]);
  // Corrección manual controlada de marca/bodega por serial (auditoría de
  // inventario, fase 2): permite que un usuario de bodega complete estos
  // dos campos directamente desde la pantalla, en vez de requerir un
  // cambio de datos por fuera del sistema cada vez que falten.
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [editingSerialRowId, setEditingSerialRowId] = useState(null);
  const [serialEditForm, setSerialEditForm] = useState({ brand: '', warehouse_id: '' });
  const [savingSerialEdit, setSavingSerialEdit] = useState(false);

  const filteredSerials = useMemo(() => {
    const f = serialsFilter;
    const rows = Array.isArray(serialsRows) ? serialsRows : [];
    const keyOf = (s) => String(s?.serial || '').trim();
    const score = (s) => {
      const ced = String(
        s.owner_dni
        || serialsCedulasMap[s.id]
        || serialsCedulasMap[s.brand_item_id]
        || serialsCedulasMap[String(s.serial || '').trim()]
        || ''
      ).trim();
      let sc = 0;
      if (ced) sc += 5; // prefer asignado
      if (s.owner_dni) sc += 2;
      if (s.brand_item_id) sc += 1;
      return sc;
    };
    const pickBySerial = new Map();
    rows.forEach((s) => {
      const ser = keyOf(s);
      if (!ser) return;
      const prev = pickBySerial.get(ser);
      if (!prev) { pickBySerial.set(ser, s); return; }
      const sp = score(prev);
      const sn = score(s);
      if (sn > sp) { pickBySerial.set(ser, s); return; }
      if (sn === sp) {
        // desempate: preferir el que tenga fecha más reciente si la conocemos
        const dp = serialsDatesMap[prev.id] || prev.last_moved_at || '';
        const dn = serialsDatesMap[s.id] || s.last_moved_at || '';
        if (dn && (!dp || String(dn) > String(dp))) pickBySerial.set(ser, s);
      }
    });
    const base = Array.from(pickBySerial.values());
    return base.filter((s) => {
      const displayBrand = (s.display_brand || s.brand || serialsMaterial?.brand || '');
      const ced = String(
        s.owner_dni
        || serialsCedulasMap[s.id]
        || serialsCedulasMap[s.brand_item_id]
        || serialsCedulasMap[String(s.serial || '').trim()]
        || ''
      ).trim();
      const nm = ced ? String(serialsNamesByDni[ced] || '').trim() : '';
      const respText = `${ced} ${nm}`.trim();
      const isIncomplete = !String(displayBrand).trim() || !String(s.warehouse_id || '').trim();
      return (
        (!f.serial || String(s.serial || '').toLowerCase().includes(f.serial.toLowerCase())) &&
        (!f.brand || String(displayBrand).toLowerCase().includes(f.brand.toLowerCase())) &&
        (!f.warehouse || String(s.warehouse_id || '').toLowerCase().includes(f.warehouse.toLowerCase())) &&
        (!f.cedula || respText.toLowerCase().includes(f.cedula.toLowerCase())) &&
        (!onlyIncomplete || isIncomplete)
      );
    });
  }, [serialsRows, serialsFilter, serialsCedulasMap, serialsMaterial, serialsNamesByDni, serialsDatesMap, onlyIncomplete]);

  const materialsById = useMemo(() => {
    const map = new Map();
    (materials || []).forEach((m) => { map.set(Number(m.id), m); });
    return map;
  }, [materials]);

  const startEditSerial = (row) => {
    setEditingSerialRowId(row.id);
    setSerialEditForm({
      brand: String(row.display_brand || row.brand || '').trim(),
      warehouse_id: String(row.warehouse_id || '').trim(),
    });
  };

  const cancelEditSerial = () => {
    setEditingSerialRowId(null);
    setSerialEditForm({ brand: '', warehouse_id: '' });
  };

  // Guarda marca/bodega de un serial puntual — corrección manual controlada
  // (auditoría de inventario, fase 2): reenvía TODOS los campos propios del
  // brand_item (no solo los editados), porque brand_items.php sobrescribe
  // material_code/brand/model/serial/status/material_type de forma directa
  // (no COALESCE) y omitirlos los pondría en NULL sin querer.
  const saveSerialEdit = async (row) => {
    setSavingSerialEdit(true);
    try {
      const res = await api.brandItems.upsert({
        id: row.brand_item_id || row.id,
        material_code: row.material_code || serialsMaterial?.code || '',
        brand: serialEditForm.brand.trim() || null,
        model: row.model ?? null,
        serial: row.serial || null,
        status: row.status || 'Activo',
        material_type: row.material_type ?? null,
        warehouse_id: serialEditForm.warehouse_id.trim() || null,
      });
      if (!res?.ok) throw new Error(res?.error || 'No fue posible guardar');
      setSerialsRows((prev) => prev.map((s) => (
        s.id === row.id
          ? { ...s, brand: serialEditForm.brand.trim(), display_brand: serialEditForm.brand.trim(), warehouse_id: serialEditForm.warehouse_id.trim() }
          : s
      )));
      setSerialsStatus({ type: 'success', text: 'Marca/bodega actualizadas correctamente.' });
      cancelEditSerial();
    } catch (e) {
      setSerialsStatus({ type: 'error', text: e?.message || 'Error guardando la corrección.' });
    } finally {
      setSavingSerialEdit(false);
      setTimeout(() => setSerialsStatus(null), 3000);
    }
  };

  // Plantillas sugeridas para agregar rápido materiales comunes
  const recommendedTemplates = [
    { code: 'TEC-01', name: 'COMPUTADORA PORTÁTIL', brand: '', category: 'Equipos', unit: 'Unidad', stockMin: 0, stockActual: 0, stockMax: 0, location: '', status: 'Activo', price: 0, serial: '', stockByWarehouse: { popayan: 0, santander: 0 } },
    { code: 'TEC-02', name: 'MONITOR', brand: '', category: 'Periféricos', unit: 'Unidad', stockMin: 0, stockActual: 0, stockMax: 0, location: '', status: 'Activo', price: 0, serial: '', stockByWarehouse: { popayan: 0, santander: 0 } },
    { code: 'TEC-03', name: 'COMBO MOUSE Y TECLADO', brand: '', category: 'Periféricos', unit: 'Unidad', stockMin: 0, stockActual: 0, stockMax: 0, location: '', status: 'Activo', price: 0, serial: '', stockByWarehouse: { popayan: 0, santander: 0 } },
    { code: 'TEC-04', name: 'IMPRESORA', brand: '', category: 'Impresoras', unit: 'Unidad', stockMin: 0, stockActual: 0, stockMax: 0, location: '', status: 'Activo', price: 0, serial: '', stockByWarehouse: { popayan: 0, santander: 0 } },
    { code: 'TEC-05', name: 'SOPORTE MONITOR Y PORTÁTIL', brand: '', category: 'Accesorios', unit: 'Unidad', stockMin: 0, stockActual: 0, stockMax: 0, location: '', status: 'Activo', price: 0, serial: '', stockByWarehouse: { popayan: 0, santander: 0 } },
  ];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = materials || [];
    if (q) {
      list = list.filter((m) =>
        [m.code, m.name, m.brand, m.category, m.location, m.serial]
          .some((v) => String(v ?? '').toLowerCase().includes(q))
      );
    }
    const stockOf = (m, wid) => {
      const sbw = m?.stockByWarehouse || {};
      const widKey = normalizeWarehouseKey(wid);
      const direct = sbw?.[wid];
      if (typeof direct !== 'undefined') return parseInt(direct) || 0;
      for (const k of Object.keys(sbw)) {
        if (normalizeWarehouseKey(k) === widKey) return parseInt(sbw[k]) || 0;
      }
      return 0;
    };
    if (warehouseFilter !== 'all') {
      const wid = warehouseFilter;
      // Cuando se filtra por bodega, mostramos solo el stock de esa bodega en 'stockActual' para visualización
      list = list
        .map(m => {
          // Calculamos el stock específico de la bodega seleccionada
          const val = stockOf(m, wid);
          // Retornamos una copia con el stock ajustado
          return { ...m, stockActual: val };
        })
        .filter(m => m.stockActual > 0);
    } else {
      // Sin filtro de bodega: mostrar stock total sumado de todas las bodegas
      list = list
        .map(m => {
           const total = Object.values(m.stockByWarehouse || {}).reduce((acc, v) => acc + (parseInt(v) || 0), 0);
           return { ...m, stockActual: total };
        })
        .filter(m => m.stockActual > 0);
    }
    if (categoryFilter !== 'Todos') {
      const f = norm(categoryFilter);
      list = list.filter((m) => norm(m.category || m.type || '').includes(f));
    }
    return list;
  }, [query, materials, warehouseFilter, categoryFilter]);

  // Fase 2 (costeo real): valor del inventario mostrado, usando el costo
  // promedio ponderado que ya trae cada material (avgCost) — null en
  // materiales que todavía no han recibido ninguna entrada con precio de
  // OC, así que se cuentan aparte en vez de sumarlos como si costaran 0.
  const inventoryValueStats = useMemo(() => {
    let totalValue = 0;
    let costedCount = 0;
    let uncostedCount = 0;
    (filtered || []).forEach((m) => {
      const hasCost = m.avgCost !== null && m.avgCost !== undefined && Number(m.avgCost) > 0;
      if (hasCost) {
        totalValue += (Number(m.stockActual) || 0) * Number(m.avgCost);
        costedCount += 1;
      } else {
        uncostedCount += 1;
      }
    });
    return { totalValue, costedCount, uncostedCount };
  }, [filtered]);

  const warehouseOptions = useMemo(() => {
    const base = Array.isArray(warehouses) ? warehouses : [];
    const normalized = base.map((w) => {
      const id = String(w.id);
      const nm = w.name || id;
      const name = nm.startsWith('Bodega') ? nm : `Bodega ${nm}`;
      return { id, name };
    });
    if (normalized.length) {
      const seen = new Set();
      return normalized
        .filter((w) => { const k = w.id; if (seen.has(k)) return false; seen.add(k); return true; })
        .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    }
    const ids = new Set();
    (materials || []).forEach((m) => {
      Object.keys(m.stockByWarehouse || {}).forEach((k) => { ids.add(String(k)); });
    });
    const derived = Array.from(ids).map((id) => ({ id, name: id === 'popayan' ? 'Bodega Popayán' : (id === 'santander' ? 'Bodega Santander' : id) }));
    if (derived.length) return derived;
    return [
      { id: 'popayan', name: 'Bodega Popayán' },
      { id: 'santander', name: 'Bodega Santander' },
    ];
  }, [warehouses, materials]);

  const warehouseById = useMemo(() => {
    const map = new Map();
    (warehouses || []).forEach((w) => { map.set(String(w.id), w); });
    return map;
  }, [warehouses]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    (materials || []).forEach((m) => {
      const c = String(m.category || m.type || '').trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [materials]);

  // "Nuevo" no se usa en UI actual; se elimina función no utilizada.

  const openEdit = (m) => {
    setEditing(m);
    setIsOpen(true);
  };

  const openFromTemplate = (tpl) => {
    // Abre el formulario precargado con la plantilla sugerida para permitir ajustes antes de guardar
    setEditing({ ...tpl });
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setEditing(null);
  };

  const handleSave = (data) => {
    const isEditExisting = editing && typeof editing.id !== 'undefined';
    if (isEditExisting) {
      onUpdate(editing.id, data);
    } else {
      onAdd(data);
    }
    closeModal();
  };

  // Plantillas no usadas; se elimina función no utilizada.

  const stockBadge = (m) => {
    const low = typeof m.stockMin === 'number' && m.stockActual <= m.stockMin;
    const base = 'px-2 py-1 rounded-full text-xs font-medium';
    const cls = low ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-green-100 text-green-700 border border-green-200';
    return (
      <span className={`${base} ${cls}`}>{m.stockActual} {m.unit}</span>
    );
  };

  const statusBadge = (status) => {
    const base = 'px-2 py-1 rounded-full text-xs font-medium';
    const cls = status === 'Activo' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-700 border border-gray-200';
    return <span className={`${base} ${cls}`}>{status}</span>;
  };

  const exportToXlsx = () => {
    try {
      const data = (filtered || []).map((m) => ({
        'Código': m.code || '',
        'Nombre': m.name || '',
        'Marca': m.brand || '',
        'Serie': (m.serial || '').trim() || '',
        'Categoría': m.category || '',
        'Stock actual': typeof m.stockActual === 'number' ? m.stockActual : '',
        'Unidad': m.unit || '',
        'Stock mínimo': typeof m.stockMin === 'number' ? m.stockMin : '',
        'Stock máximo': typeof m.stockMax === 'number' ? m.stockMax : '',
        'Costo promedio': (m.avgCost !== null && m.avgCost !== undefined && Number(m.avgCost) > 0) ? Number(m.avgCost) : '',
        'Valor': (m.avgCost !== null && m.avgCost !== undefined && Number(m.avgCost) > 0) ? (Number(m.stockActual) || 0) * Number(m.avgCost) : '',
        'Ubicación': m.location || '',
        'Estado': m.status || '',
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Existencias');
      const filename = `existencias_${new Date().toISOString().slice(0,10)}.xlsx`;
      void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'almacen', reportName: 'Existencias de almacén' });
      XLSX.writeFile(wb, filename);
    } catch (err) {
      console.error('Error exportando existencias a .xlsx', err);
      alert('No se pudo exportar el archivo .xlsx.');
    }
  };

  const toDateInputValue = (raw) => {
    if (!raw) return '';
    try {
      const d = new Date(raw);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const da = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${da}`;
    } catch (_) {
      const s = String(raw);
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      return '';
    }
  };

  const handleDeleteMaterial = async (m) => {
    if (!m) return;
    try {
      await Promise.resolve(onDelete(m.id));
      setMaterialStatus({ type: 'success', text: `Código ${m.code || ''} eliminado con éxito` });
      setTimeout(() => setMaterialStatus(null), 2500);
    } catch (e) {
      setMaterialStatus({ type: 'error', text: e?.message || 'No fue posible eliminar el material' });
      setTimeout(() => setMaterialStatus(null), 3500);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between w-full mb-6">
        <h3 className="section-title">Gestión de Stock</h3>
      <div className="flex items-center gap-2">
        <div>
          <select
            value={warehouseFilter}
            onChange={(e) => setWarehouseFilter(e.target.value)}
            className="border rounded px-3 py-2 text-sm bg-white"
          >
            <option value="all">Todas las bodegas</option>
            {warehouseOptions.map((w) => (
              <option key={w.id} value={String(w.id)}>{w.name}</option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border rounded px-3 py-2 text-sm bg-white"
          >
            <option value="Todos">Todas las categorías</option>
            {categoryOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              const resp = await api.stockByWarehouse.get({ limit: 1000, order: 'asc' });
              const rows = Array.isArray(resp?.data) ? resp.data : (resp?.data ? [resp.data] : []);
              const positive = rows.filter((r) => (parseInt(r.stock) || 0) > 0);
              setStockAllRows(positive);
              } catch (err) {
                console.error('Error al obtener stock completo', err);
                setStockAllRows([]);
              }
              setStockAllOpen(true);
            }}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Stock completo
          </button>
          <button
            type="button"
            onClick={exportToXlsx}
            className="inline-flex items-center gap-2 text-white px-4 py-2 rounded"
            style={{ backgroundColor: '#04873b' }}
          >
            Descargar
          </button>
        </div>
      </div>
      {materialStatus && (
        <div className={`mb-4 px-3 py-2 rounded border text-sm ${materialStatus.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
          {materialStatus.text}
        </div>
      )}

      <SearchBar
        value={query}
        onChange={setQuery}
        placeholder="Buscar por nombre o código..."
      />

      {showSuggested && (
        <div className="mt-4 mb-6 border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700">Plantillas sugeridas</h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {recommendedTemplates.map((tpl) => (
              <div key={tpl.code} className="border rounded-lg bg-white p-3 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{tpl.name}</p>
                  <p className="text-xs text-gray-500">Código: {tpl.code} · {tpl.category}</p>
                </div>
                <button
                  type="button"
                  onClick={() => openFromTemplate(tpl)}
                  className="ml-3 text-sm px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap"
                >
                  Agregar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 mb-4 bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-center gap-6">
        <div>
          <div className="text-[11px] text-gray-500 uppercase tracking-wide">Valor de inventario (filtro actual)</div>
          <div className="text-xl font-semibold text-gray-900">{formatMoney(inventoryValueStats.totalValue)}</div>
        </div>
        <div className="text-xs text-gray-500 max-w-xs">
          {inventoryValueStats.costedCount} material(es) con costo calculado
          {inventoryValueStats.uncostedCount > 0
            ? ` · ${inventoryValueStats.uncostedCount} sin costo todavía (recepción de OC con bodega en Contabilidad o Verificar llegada)`
            : ''}
        </div>
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-700">
              <th className="px-4 py-2 text-left">Código</th>
              <th className="px-4 py-2 text-left">Nombre</th>
              <th className="px-4 py-2 text-left">Marca</th>
              <th className="px-4 py-2 text-left">Serie</th>
              <th className="px-4 py-2 text-left">Categoría</th>
              <th className="px-4 py-2 text-left">Stock</th>
              <th className="px-4 py-2 text-right">Costo prom.</th>
              <th className="px-4 py-2 text-right">Valor</th>
              <th className="px-4 py-2 text-left">Ubicación</th>
              <th className="px-4 py-2 text-left">Estado</th>
              <th className="px-4 py-2 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} className="border-t">
                <td className="px-4 py-2">{m.code}</td>
                <td className="px-4 py-2">{m.name}</td>
                <td className="px-4 py-2">{m.brand || ''}</td>
                <td className="px-4 py-2">
                  {(() => {
                    const v = (m.serial || '').trim();
                    return /^NS-\d+-\d+$/.test(v) ? 'Sin serial' : (v || '-');
                  })()}
                  <button
                    type="button"
                    title="Ver seriales disponibles"
                    className="ml-2 text-blue-600 hover:text-blue-700 underline"
                    onClick={async () => {
                      try {
                        const mcode = m?.code || m?.material_code || '';
                        const mid = m?.id || m?.materialId || m?.code || '';
                        const wid = warehouseFilter !== 'all' ? warehouseFilter : '';

                        let requiresSerial = Boolean(m?.requiresSerial) || String(m?.hasSerial || '').toLowerCase() === 'sí';
                        if (!requiresSerial) {
                          try {
                            const biResp = await api.brandItems.get({ material_code: mcode, material_id: mid, warehouse_id: wid, include_deleted: 1, limit: 1, offset: 0, order: 'asc' });
                            const biRows = Array.isArray(biResp?.data) ? biResp.data : (biResp?.data ? [biResp.data] : []);
                            const hasAnySerial = biRows.some((r) => String(r?.serial || '').trim());
                            if (hasAnySerial) requiresSerial = true;
                          } catch (_) {}
                        }

                        setSerialsMaterial({ ...m, requiresSerial });
                        if (!requiresSerial) {
                          const total = Number(m?.stockActual ?? 0) || 0;
                          const byWarehouse = Object.entries(m?.stockByWarehouse || {}).map(([wid, qty]) => ({
                            warehouse_id: String(wid),
                            stock: Number(qty) || 0
                          }));
                          setNonSerialInfo({ total, byWarehouse });
                          setSerialsRows([]);
                          setSerialsEdits({});
                          setSerialsFilter({ serial: '', brand: '', warehouse: '', cedula: '' });
                          setSerialsCedulasMap({});
                          setSerialsDatesMap({});
                          setSerialsMovDatesById({});
                          setSerialsStatus(null);
                          setSerialsTotal(total);
                          setSerialsOpen(true);
                          return;
                        }

                        const pageLimit = 10000;
                        const normalizeSerialKey = (v) => String(v || '').trim();

                        const fetchAllPages = async (fetchPage, source) => {
                          let acc = [];
                          let off = 0;
                          const seen = new Set();
                          for (let iter = 0; iter < 500; iter += 1) {
                            const { rows, step } = await fetchPage(off);
                            const arr = Array.isArray(rows) ? rows : [];
                            if (arr.length === 0) break;
                            let added = 0;
                            arr.forEach((r) => {
                              const key = `${String(source || '')}:${String(r?.id ?? '')}:${normalizeSerialKey(r?.serial)}`;
                              if (!key) return;
                              if (seen.has(key)) return;
                              seen.add(key);
                              acc.push({ ...r, _source: source });
                              added += 1;
                            });
                            off += (typeof step === 'number' && step > 0) ? step : arr.length;
                            if (added === 0) break;
                          }
                          return acc;
                        };

                        const brandItemsFromApi = await fetchAllPages(async (offset) => {
                          const resp = await api.brandItems.get({ material_code: mcode, material_id: mid, warehouse_id: wid, include_deleted: 1, limit: pageLimit, offset, order: 'asc' });
                          const rows = Array.isArray(resp?.data) ? resp.data : (resp?.data ? [resp.data] : []);
                          return { rows, step: rows.length };
                        }, 'brand_items');

                        const all = brandItemsFromApi.map((bi, idx) => ({
                          id: bi?.id ?? `brand_items:${idx}:${normalizeSerialKey(bi?.serial)}`,
                          brand_item_id: bi?.id ?? null,
                          material_code: bi?.material_code ?? mcode,
                          serial: normalizeSerialKey(bi?.serial),
                          brand: bi?.brand ?? '',
                          display_brand: (bi?.brand ?? '').trim(),
                          // model y material_type no se muestran en esta tabla, pero se
                          // conservan igual: brand_items.php sobrescribe estos campos de
                          // forma directa (no COALESCE) en cada guardado, así que hay que
                          // reenviar su valor actual al corregir marca/bodega, o se pierden.
                          model: bi?.model ?? null,
                          material_type: bi?.material_type ?? null,
                          warehouse_id: bi?.warehouse_id ?? '',
                          location: bi?.location ?? '',
                          warranty_until: bi?.warranty_until ?? '',
                          certificate_no: bi?.certificate_no ?? '',
                          status: bi?.status ?? '',
                          _source: 'brand_items'
                        })).filter((r) => normalizeSerialKey(r?.serial));
                        setSerialsRows(all);
                        setSerialsTotal(all.length);
                        setNonSerialInfo(null);
                        const init = {};
                        const initDates = {};
                        all.forEach((s) => {
                          init[s.id] = { location: s.location || '', warranty_until: s.warranty_until || '', certificate_no: s.certificate_no || '' };
                          if (s.last_moved_at) { initDates[s.id] = s.last_moved_at; }
                        });
                        setSerialsEdits(init);
                        setSerialsDatesMap(initDates);
                        const map = {};
                        try {
                          const brandItemIds = Array.from(new Set(all.map((s) => String(s?.brand_item_id || s?.id || '')).filter(Boolean)));

                          // La tabla assignments es un historial que se sigue insertando
                          // sin "cerrar" nunca la fila anterior — una devolución NO se
                          // marca ahí, solo queda registrada como movimiento tipo
                          // "devolucion". Antes esto hacía que el responsable mostrado
                          // fuera el de CUALQUIER asignación encontrada (tabla assignments,
                          // o si no había, el primer movimiento tipo "asignación"), sin
                          // mirar si después hubo una devolución — mostrando responsables
                          // desactualizados para equipos que ya se devolvieron. Ahora se
                          // combinan ambas fuentes en un solo historial de eventos por
                          // serial y se toma el MÁS RECIENTE de todos: si ese último
                          // evento es una asignación, esa persona es el responsable
                          // vigente; si es una devolución (u otra salida), el ítem ya no
                          // tiene responsable aunque alguna vez se le haya asignado.
                          const events = [];
                          if (brandItemIds.length > 0) {
                            const asgResp = await api.assignments.get({ brand_item_ids: brandItemIds.join(','), include_deleted: 0, order: 'desc', limit: 10000 });
                            const asgs = Array.isArray(asgResp?.data) ? asgResp.data : (asgResp?.data ? [asgResp.data] : []);
                            asgs.forEach((a) => {
                              const bid = a?.brand_item_id || a?.brandItemId;
                              const dni = String(a?.person_dni || a?.dni || '').trim();
                              if (!bid || !dni) return;
                              events.push({ bid: String(bid), dni, type: 'asignacion', at: String(a?.created_at || '') });
                            });
                          }
                          const movsResp = await api.movements.get({ material_id: mid, order: 'desc', limit: 2000 });
                          const movs = Array.isArray(movsResp?.data) ? movsResp.data : (movsResp?.data ? [movsResp.data] : []);
                          movs.forEach((mv) => {
                            const bid = mv?.brand_item_id || mv?.brandItemId || mv?.brand_item || mv?.item_id;
                            if (!bid) return;
                            const type = String(mv?.type || '').toLowerCase();
                            const dni = String(mv?.requester_dni || mv?.dni || mv?.person_id || mv?.responsibleDoc || mv?.requester_doc || mv?.requester_id || '').trim();
                            events.push({ bid: String(bid), dni, type, at: String(mv?.created_at || '') });
                          });

                          const latestByBid = new Map();
                          events.forEach((ev) => {
                            if (!ev.at) return;
                            const prev = latestByBid.get(ev.bid);
                            if (!prev || ev.at > prev.at) latestByBid.set(ev.bid, ev);
                          });
                          latestByBid.forEach((ev, bid) => {
                            const isAssign = ev.type === 'asignacion' || ev.type.includes('asignaci');
                            if (!isAssign || !ev.dni) return;
                            map[bid] = ev.dni;
                            const serialRow = all.find((s) => String(s?.brand_item_id || s?.id) === bid);
                            if (serialRow?.serial) map[String(serialRow.serial)] = ev.dni;
                          });
                        } catch (_) {}
                        setSerialsCedulasMap(map);
                        try {
                          if (Object.keys(serialsNamesByDni || {}).length === 0) {
                            const pRes = await api.people.get({ limit: 10000, clean: 1 });
                            const persons = Array.isArray(pRes?.data?.data)
                              ? pRes.data.data
                              : (Array.isArray(pRes?.data) ? pRes.data : (pRes?.data ? [pRes.data] : []));
                            const nmMap = {};
                            persons.forEach((p) => {
                              const dni = String(p?.dni || p?.id || '').trim();
                              const name = String(p?.name || p?.nombre_completo || '').trim();
                              if (dni && name) nmMap[dni] = name;
                            });
                            setSerialsNamesByDni(nmMap);
                          }
                        } catch (_) {}
                        // Obtener fechas de último movimiento por brand_item y por ID de movimiento
                        try {
                          const allMovsResp = await api.movements.get({ material_id: mid, order: 'desc', limit: 1000 });
                          const allMovs = Array.isArray(allMovsResp?.data) ? allMovsResp.data : (allMovsResp?.data ? [allMovsResp?.data] : []);
                          const dmap = { ...initDates };
                          const movDates = {};
                          allMovs.forEach((mv) => {
                            const bid = mv?.brand_item_id || mv?.brandItemId || mv?.brand_item || mv?.item_id;
                            const created = mv?.created_at;
                            if (bid && created && !dmap[bid]) { dmap[bid] = created; }
                            const midMv = mv?.id;
                            if (midMv && created && !movDates[midMv]) { movDates[midMv] = created; }
                          });
                          setSerialsDatesMap(dmap);
                          setSerialsMovDatesById(movDates);
                        } catch (_) { /* opcional */ }
                        setSerialsOpen(true);
                      } catch (e) {
                        console.error('fetch serials error', e);
                        setSerialsRows([]);
                        setSerialsOpen(true);
                      }
                    }}
                  >Ver</button>
                </td>
                <td className="px-4 py-2">{m.category}</td>
                <td className="px-4 py-2">{stockBadge(m)}</td>
                <td className="px-4 py-2 text-right font-mono">
                  {(m.avgCost !== null && m.avgCost !== undefined && Number(m.avgCost) > 0)
                    ? formatMoney(m.avgCost)
                    : <span className="text-gray-400">-</span>}
                </td>
                <td className="px-4 py-2 text-right font-mono">
                  {(m.avgCost !== null && m.avgCost !== undefined && Number(m.avgCost) > 0)
                    ? formatMoney((Number(m.stockActual) || 0) * Number(m.avgCost))
                    : <span className="text-gray-400">-</span>}
                </td>
                <td className="px-4 py-2">{m.location}</td>
                <td className="px-4 py-2">{statusBadge(m.status)}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-700"
                      onClick={() => openEdit(m)}
                      aria-label="Editar"
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      type="button"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDeleteMaterial(m)}
                      aria-label="Eliminar"
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-500">No se encontraron registros de stock.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={serialsOpen}
        title={`${serialsMaterial?.requiresSerial ? 'Seriales' : 'Disponibles'} del material: ${serialsMaterial?.name || ''}`}
        onClose={() => { setSerialsOpen(false); setOnlyIncomplete(false); cancelEditSerial(); }}
      >
                  {/* Vista simplificada: tabla con Serial, Marca, Código de Bodega, Responsable y Fecha */}
          {serialsMaterial?.requiresSerial && (
            <div className="mb-2 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-gray-500">
                Mostrando {filteredSerials.length} de {serialsTotal || filteredSerials.length}
              </div>
              <label className="text-xs text-gray-700 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyIncomplete}
                  onChange={(e) => setOnlyIncomplete(e.target.checked)}
                />
                Solo sin marca o sin bodega
              </label>
            </div>
          )}
          {serialsStatus && (
            <div
              className={`mb-3 px-3 py-2 rounded border text-sm ${serialsStatus.type === 'success' ? 'bg-green-100 text-green-800 border-green-300' : 'bg-red-100 text-red-800 border-red-300'}`}
            >
              {serialsStatus.text}
            </div>
          )}
          {!serialsMaterial?.requiresSerial ? (
            <div className="space-y-3">
              <div className="text-sm text-gray-700">
                Unidades disponibles: <span className="font-semibold">{Number(nonSerialInfo?.total ?? 0) || 0}</span>
              </div>
              {warehouseFilter === 'all' && Array.isArray(nonSerialInfo?.byWarehouse) && nonSerialInfo.byWarehouse.length > 0 && (
                <div className="overflow-x-auto border rounded">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100 text-gray-700">
                        <th className="px-4 py-2 text-left">Bodega</th>
                        <th className="px-4 py-2 text-left">Disponible</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nonSerialInfo.byWarehouse.map((r, idx) => (
                        <tr key={`${r.warehouse_id}-${idx}`} className="border-t">
                          <td className="px-4 py-2">{warehouseById.get(String(r.warehouse_id))?.name || String(r.warehouse_id)}</td>
                          <td className="px-4 py-2">{Number(r.stock) || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto border rounded">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-gray-700">
                    <th className="px-4 py-2 text-left">Serial</th>
                    <th className="px-4 py-2 text-left">Marca</th>
                    <th className="px-4 py-2 text-left">Bodega</th>
                    <th className="px-4 py-2 text-left">Responsable</th>
                    <th className="px-4 py-2 text-left">Vence</th>
                    <th className="px-4 py-2 text-left">Fecha</th>
                    <th className="px-4 py-2 text-left">Certificado</th>
                    <th className="px-4 py-2 text-left">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSerials.length === 0 && (
                    <tr><td className="px-4 py-4" colSpan={8}>No hay seriales.</td></tr>
                  )}
                  {filteredSerials.map((s) => (
                    <tr key={s.id} className="border-t">
                      <td className="px-4 py-2" style={{ minWidth: '420px' }}>{(() => {
                        const v = (s.serial || '').trim();
                        const content = /^NS-\d+-\d+$/.test(v) ? 'Sin serial' : v;
                        return (
                          <div className="font-mono whitespace-nowrap" title={content}>{content}</div>
                        );
                      })()}</td>
                      <td className="px-4 py-2">
                        {editingSerialRowId === s.id ? (
                          <input
                            type="text"
                            value={serialEditForm.brand}
                            onChange={(e) => setSerialEditForm((f) => ({ ...f, brand: e.target.value }))}
                            placeholder="Marca"
                            className="border rounded px-2 py-1 text-sm w-32"
                          />
                        ) : (
                          (() => {
                            const v = (s.display_brand || s.brand || serialsMaterial?.brand || '').trim();
                            return v ? v : <span className="text-amber-600">Sin marca</span>;
                          })()
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {editingSerialRowId === s.id ? (
                          <select
                            value={serialEditForm.warehouse_id}
                            onChange={(e) => setSerialEditForm((f) => ({ ...f, warehouse_id: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm"
                          >
                            <option value="">Seleccione...</option>
                            {(warehouseOptions || []).map((w) => (
                              <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                          </select>
                        ) : (
                          (() => {
                            const id = String(s.warehouse_id || '');
                            if (!id) return <span className="text-amber-600">Sin bodega</span>;
                            const wh = warehouseById.get(id);
                            return wh?.name || id;
                          })()
                        )}
                      </td>
                      <td className="px-4 py-2">{(() => {
                        const ced = String(
                          s.owner_dni
                          || serialsCedulasMap[s.id]
                          || serialsCedulasMap[s.brand_item_id]
                          || serialsCedulasMap[String(s.serial || '').trim()]
                          || ''
                        ).trim();
                        if (!ced) return 'Disponible';
                        const apiName = String(s.owner_name || '').trim();
                        const nm = apiName || String(serialsNamesByDni[ced] || '').trim();
                        return nm ? `${ced} - ${nm}` : ced;
                      })()}</td>
                      <td className="px-4 py-2">{(() => {
                        const raw = (serialsEdits?.[s.id]?.warranty_until) || s.warranty_until || '';
                        const v = toDateInputValue(raw);
                        return v || '';
                      })()}</td>
                      <td className="px-4 py-2">{(() => {
                        const raw = serialsDatesMap[s.id] || s.last_moved_at || '';
                        if (raw) {
                          try { return new Date(raw).toLocaleString('es-CO', { timeZone: 'America/Bogota' }); } catch (_) { return String(raw); }
                        }
                        const ser = String(s.serial || '').trim();
                        const m = ser.match(/^NS-(\d+)-\d+$/);
                        if (m) {
                          const movId = parseInt(m[1], 10);
                          const created = serialsMovDatesById[movId];
                          if (created) {
                            try { return new Date(created).toLocaleString('es-CO', { timeZone: 'America/Bogota' }); } catch (_) { return String(created); }
                          }
                        }
                        return '';
                      })()}</td>
                      <td className="px-4 py-2">{(serialsEdits?.[s.id]?.certificate_no) ?? (s.certificate_no || '')}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {editingSerialRowId === s.id ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="text-green-700 hover:text-green-800 text-xs font-medium"
                              disabled={savingSerialEdit}
                              onClick={() => saveSerialEdit(s)}
                            >
                              {savingSerialEdit ? 'Guardando...' : 'Guardar'}
                            </button>
                            <button
                              type="button"
                              className="text-gray-500 hover:text-gray-700 text-xs"
                              disabled={savingSerialEdit}
                              onClick={cancelEditSerial}
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="text-blue-600 hover:text-blue-700"
                            aria-label="Editar marca y bodega"
                            title="Editar marca y bodega"
                            onClick={() => startEditSerial(s)}
                          >
                            <Pencil size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal>

      {/* Modal: Stock completo */}
      <Modal isOpen={stockAllOpen} title="Stock completo" onClose={() => setStockAllOpen(false)}>
        <div className="overflow-x-auto border rounded">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-700">
                <th className="px-4 py-2 text-left">Código</th>
                <th className="px-4 py-2 text-left">Material</th>
                <th className="px-4 py-2 text-left">Marca</th>
                <th className="px-4 py-2 text-left">Bodega</th>
                <th className="px-4 py-2 text-left">Stock</th>
              </tr>
            </thead>
            <tbody>
              {(stockAllRows || []).length === 0 && (
                <tr><td className="px-4 py-4" colSpan={5}>No hay registros de stock.</td></tr>
              )}
              {(stockAllRows || []).map((r, idx) => {
                const mat = materialsById.get(Number(r.material_id));
                return (
                  <tr key={`${r.material_id}-${r.warehouse_id}-${idx}`} className="border-t">
                    <td className="px-4 py-2">{r.material_code || mat?.code || ''}</td>
                    <td className="px-4 py-2">{r.material_name || mat?.name || ''}</td>
                    <td className="px-4 py-2">{mat?.brand || ''}</td>
                    <td className="px-4 py-2">{r.warehouse_id || '-'}</td>
                    <td className="px-4 py-2">{typeof r.stock === 'number' ? r.stock : (parseInt(r.stock) || 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Modal>

      <Modal isOpen={isOpen} onClose={closeModal} title={editing ? 'Editar Stock' : 'Nuevo Stock'}>
        <StockForm material={editing} onSave={handleSave} onCancel={closeModal} />
      </Modal>
    </div>
  );
}
